import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  CardHeader,
  Autocomplete,
  Typography,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Chip,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import useNswagClient from '../../../hooks/api/useNswagClient';
import {
  CreateModuleRequest,
  UpdateModuleRequest,
  PermissionDetail,
} from '../../../app/services/api/generated';

interface SystemOption {
  id: number;
  name: string;
}

const ModuleForm = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const { getModuleById, createModule, updateModule, getSystems, getPermissions } = useNswagClient();
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedSystem, setSelectedSystem] = useState<SystemOption | null>(null);
  const [premium, setPremium] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionDetail[]>([]);

  const [systems, setSystems] = useState<SystemOption[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionDetail[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchSystems = async () => {
    try {
      const response = await getSystems();
      if (response?.data?.systems) {
        setSystems(response.data.systems.map((s: { id?: number; name?: string }) => ({
          id: s.id ?? 0,
          name: s.name ?? '',
        })));
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await getPermissions();
      if (response?.data?.permissions) {
        setAllPermissions(response.data.permissions);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchModule = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const response = await getModuleById(parseInt(id));
      if (response?.data) {
        const mod = response.data;
        setName(mod.name ?? '');
        setDescription(mod.description ?? '');
        setPremium(mod.premium ?? false);
        if (mod.systemId) {
          setSelectedSystem({ id: mod.systemId, name: mod.systemName ?? '' });
        }
        if (mod.permissions) {
          setSelectedPermissions(mod.permissions.map((p: { id?: number; name?: string; description?: string }) => ({
            id: p.id ?? 0,
            name: p.name ?? '',
            description: p.description ?? '',
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
    fetchPermissions();
    if (isEdit) {
      fetchModule();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name?.trim()) {
      newErrors.name = t('modules.nameRequired');
    } else if (name.trim().length > 50) {
      newErrors.name = t('modules.nameTooLong');
    }

    if (description && description.length > 255) {
      newErrors.description = t('modules.descriptionTooLong');
    }

    if (!selectedSystem) {
      newErrors.systemId = t('modules.systemRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const permissionIds = selectedPermissions.map(p => p.id!);

      if (isEdit) {
        const body = {
          name: name.trim(),
          description: description.trim(),
          systemId: selectedSystem!.id,
          premium,
          permissionIds,
        } as UpdateModuleRequest;
        await updateModule(parseInt(id!), body);
        setMessage(t('modules.updateSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/modules');
        }, 500);
      } else {
        const body = {
          name: name.trim(),
          description: description.trim(),
          systemId: selectedSystem!.id,
          premium,
          permissionIds,
        } as CreateModuleRequest;
        await createModule(body);
        setMessage(t('modules.createSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/modules');
        }, 500);
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('modules.updateError') : t('modules.createError')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/modules');
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'ModuleUpdate' : 'ModuleCreate']);
  const canView = hasPermissionTo(['ModuleView']);

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('modules.accessDenied')}</PageTitle>
        <Typography>{t('modules.insufficientPermissions')}</Typography>
      </PageArea>
    );
  }

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('modules.editModule') : t('modules.createNew')}</PageTitle>
        <Typography>{t('modules.loading')}</Typography>
      </PageArea>
    );
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic Module Information */}
        <Card elevation={1}>
          <CardHeader
            title={isEdit ? t('modules.editModule') : t('modules.createNew')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={8}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('modules.name')}
                  placeholder={t('modules.namePlaceholder')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  inputProps={{ maxLength: 50 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={systems}
                  getOptionLabel={(option) => option.name}
                  value={selectedSystem}
                  onChange={(_, value) => {
                    setSelectedSystem(value);
                    if (errors.systemId) setErrors(prev => ({ ...prev, systemId: '' }));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('modules.system')}
                      placeholder={t('modules.selectSystem')}
                      error={!!errors.systemId}
                      helperText={errors.systemId}
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('modules.description')}
                  placeholder={t('modules.descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) setErrors(prev => ({ ...prev, description: '' }));
                  }}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 255 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item
                xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={premium}
                      onChange={(e) => setPremium(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('modules.premiumModule')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Permissions Selection */}
        <Card elevation={1}>
          <CardHeader
            title={t('modules.permissionsTitle')}
            subheader={t('modules.permissionsSubtitle')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Autocomplete
              multiple
              options={allPermissions}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name || ''}
              value={selectedPermissions}
              onChange={(_, value) => setSelectedPermissions(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('modules.selectPermissions')}
                  placeholder={t('modules.searchPermissions')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: 2 } }}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {t('modules.cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{ minWidth: 120, borderRadius: 2 }}
                >
                  {isSaving ? t('modules.saving') : isEdit ? t('modules.update') : t('modules.create')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default ModuleForm;
